import { Checkbox, type CheckboxProps } from '@affine/component';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import type { CSSProperties, HTMLAttributes } from 'react';

import * as styles from './selection-checkbox.css';

export type { RangeSelectionInput, SelectionChange } from './range-selection';
export {
  applyRangeSelection,
  replaceSelectionKey,
  toggleSelectionKey,
} from './range-selection';

export type SelectionCheckboxProps = CheckboxProps;

export function SelectionCheckbox({
  checked,
  className,
  indeterminate,
  ...props
}: SelectionCheckboxProps) {
  return (
    <Checkbox
      {...props}
      checked={checked}
      className={clsx(styles.selectionCheckbox, className)}
      data-checked={checked ? 'true' : undefined}
      data-indeterminate={indeterminate ? 'true' : undefined}
      indeterminate={indeterminate}
    />
  );
}

function toCssLength(value: number | string) {
  return typeof value === 'number' ? `${value}px` : value;
}

export type AnimatedSelectionSlotProps = HTMLAttributes<HTMLDivElement> & {
  checked: boolean;
  visible: boolean;
  checkboxProps?: Omit<SelectionCheckboxProps, 'checked'>;
  width?: number | string;
  gap?: number | string;
  hiddenMarginRight?: number | string;
  slotHeight?: number | string;
};

export function AnimatedSelectionSlot({
  checked,
  visible,
  checkboxProps,
  width = 20,
  gap = 12,
  hiddenMarginRight = 0,
  slotHeight = 20,
  className,
  style,
  ...props
}: AnimatedSelectionSlotProps) {
  const slotStyle: CSSProperties = {
    ...assignInlineVars({
      [styles.animatedSelectionSlotWidth]: toCssLength(width),
      [styles.animatedSelectionSlotGap]: toCssLength(gap),
      [styles.animatedSelectionSlotHiddenMarginRight]:
        toCssLength(hiddenMarginRight),
      [styles.animatedSelectionSlotHeight]: toCssLength(slotHeight),
    }),
    ...style,
  };

  return (
    <div
      className={clsx(styles.animatedSelectionSlot, className)}
      data-visible={visible ? 'true' : undefined}
      style={slotStyle}
      {...props}
    >
      <SelectionCheckbox checked={checked} {...checkboxProps} />
    </div>
  );
}
